import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { loadPlanHistory } from "../lib/preferencesApi";
import type { FuelPlanSummary } from "../lib/preferencesApi";
import { isAbortError } from "../lib/api";
import {
  useGeneratingPlanWatcher,
  type PendingPlanResolution,
  type WatchableRow
} from "./useGeneratingPlanWatcher";
import { usePlanGeneration, type TrackOptions } from "../context/planGenerationContext";

/**
 * "Stay-in-place" generation tracking for My Training (docs/ux-overhaul Phase
 * 2.4, reused by the box card in 3.6). Overview "Approach B": load the same
 * plan-history rows `/plans` feeds to {@link useGeneratingPlanWatcher} and poll
 * them here — so an in-flight build is server-tracked and survives navigation
 * or reload (a fresh mount re-loads the history and re-discovers the request).
 *
 * {@link track} lets a just-dispatched generate register its `requestId`
 * immediately (the row may not be in the freshly-loaded history yet), so the
 * poll starts without waiting for the reload round-trip. On any resolution the
 * history reloads (the request row becomes a completed plan) and — for a
 * completed build — `onComplete` fires with the new plan id.
 *
 * `onResolveAny` fires for EVERY terminal result (completed/failed/expired/
 * timeout/auth_blocked), BEFORE the completed-only `onComplete` — callers that
 * track in-flight spinners (JourneyRailContainer) use it to clear the spinner
 * and surface a failure toast (Contract A). `onComplete` stays for back-compat
 * with the completed-only path (MyTraining).
 */
export function useGeneratingPlans(
  onComplete?: (fuelPlanId: string) => void,
  onResolveAny?: (info: PendingPlanResolution) => void,
  opts?: {
    /**
     * F-01-3: when provided, the plan-history load is deferred until a principal
     * is resolved (`principalKey != null`) and re-keyed on it. This hook runs at
     * the app root (PlanGenerationProvider), OUTSIDE the auth Gate — without this
     * gate its mount effect fires `GET /api/fuel-plans/plans` before the
     * `/api/customer/context` ping has minted the anon session, spawning a
     * competing session (cookie ≠ stored carry) whose CSRF binds to the wrong
     * principal — the two first-load 403s. Omit to always load (callers that
     * already render inside the Gate, where a principal is guaranteed).
     */
    principalKey?: string | null;
  }
): {
  /** True while at least one tracked request is still building. */
  hasPending: boolean;
  /** Request IDs that resolved within the last few seconds (for a flash). */
  justResolved: ReadonlySet<string>;
  /** Register a just-dispatched request so polling starts immediately.
   *  `options` rides through to PlanGenerationProvider.track (toast label,
   *  source, journey context). */
  track: (requestId: string, options?: TrackOptions) => void;
  /** Re-arm the foreground poll for an already-pending requestId (CT-3): after
   *  a client `timeout`, a retry hands back the SAME processing id, which is
   *  still in the watched rows, so `track` alone can't restart the poll. */
  resetWatch: (requestId: string) => void;
  /** Force a plan-history reload (e.g. after an external change). */
  reload: () => void;
  /** The loaded plan-history rows — completed `plan` rows carry commerce data
   *  (`periodTotals`, `coveragePercent`) keyed by `journeyId`/`dropIndex` (3.7). */
  rows: ReadonlyArray<FuelPlanSummary>;
} {
  const [rows, setRows] = useState<FuelPlanSummary[]>([]);
  const [extraIds, setExtraIds] = useState<ReadonlySet<string>>(() => new Set());
  const [token, setToken] = useState(0);

  const reload = useCallback(() => setToken((t) => t + 1), []);

  // Stable handles to the latest callbacks so `handleResolved` (and thus the
  // watcher effect) never re-runs just because a caller passed a fresh closure.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const onResolveAnyRef = useRef(onResolveAny);
  onResolveAnyRef.current = onResolveAny;

  // F-01-3: when a caller opts into principal-gating, defer the load until a
  // principal is resolved. `opts === undefined` keeps the always-load behavior
  // for in-Gate callers; a provided-but-null principalKey means "not resolved yet".
  const principalKey = opts?.principalKey;
  const principalGated = opts !== undefined && principalKey == null;

  useEffect(() => {
    if (principalGated) return;
    const ctrl = new AbortController();
    loadPlanHistory(ctrl.signal)
      .then((r) => {
        // Defensive: this hook now runs at the app root (PlanGenerationProvider)
        // for every principal, so an unexpected/empty payload must degrade to
        // "nothing to watch", never crash the shell.
        if (!ctrl.signal.aborted) setRows(Array.isArray(r) ? r : []);
      })
      .catch((err: unknown) => {
        // Silent — this is a background convenience; the screen's own data
        // load surfaces any real auth/availability error to the user.
        if (!isAbortError(err)) return;
      });
    return () => ctrl.abort();
    // `principalKey` in deps so the history reloads when the principal changes
    // (e.g. anon → customer after login), keeping the watched rows owner-correct.
  }, [token, principalGated, principalKey]);

  // Merge history rows with optimistically-tracked request ids (deduped) so a
  // build is watched the instant it's dispatched, before the history reloads.
  const watchRows = useMemo<WatchableRow[]>(() => {
    const known = new Set(rows.map((r) => r.requestId));
    const synthetic: WatchableRow[] = [...extraIds]
      .filter((id) => !known.has(id))
      .map((id) => ({ kind: "request", requestId: id, status: "processing" }));
    return [...rows, ...synthetic];
  }, [rows, extraIds]);

  const handleResolved = useCallback(
    (info: PendingPlanResolution) => {
      // Drop the resolved id from the optimistic set and reload history so the
      // request row is replaced by its completed/failed plan row.
      setExtraIds((cur) => {
        if (!cur.has(info.requestId)) return cur;
        const next = new Set(cur);
        next.delete(info.requestId);
        return next;
      });
      reload();
      // Every terminal result fires `onResolveAny` first (spinner-clearing +
      // failure surfacing, Contract A), then the completed-only `onComplete`.
      onResolveAnyRef.current?.(info);
      if (info.result === "completed" && info.fuelPlanId) onCompleteRef.current?.(info.fuelPlanId);
    },
    [reload]
  );

  const { hasPending, justResolved, resetWatch } = useGeneratingPlanWatcher(
    watchRows,
    handleResolved
  );

  // App-root coordinator: every tracked dispatch is also registered with the
  // PlanGenerationProvider, which owns the outcome toast + cross-tab publish
  // no matter where the user navigates. Callers pass `options` (source/label/
  // journey context) so the provider's toasts can say "Fuel box 2" rather than
  // the generic copy. Inside the provider's own internal useGeneratingPlans
  // this resolves to the no-op default context.
  const planGeneration = usePlanGeneration();

  const track = useCallback(
    (requestId: string, options?: TrackOptions) => {
      if (!requestId) return;
      planGeneration.track(requestId, options);
      setExtraIds((cur) => {
        if (cur.has(requestId)) return cur;
        const next = new Set(cur);
        next.add(requestId);
        return next;
      });
    },
    [planGeneration]
  );

  const resetWatchTracked = useCallback(
    (requestId: string) => {
      if (!requestId) return;
      // Ensure the id is still in the watched set (it may have dropped from the
      // optimistic set when it resolved/timed out), then re-arm the poll clock.
      setExtraIds((cur) => {
        if (cur.has(requestId)) return cur;
        const next = new Set(cur);
        next.add(requestId);
        return next;
      });
      resetWatch(requestId);
    },
    [resetWatch]
  );

  return { hasPending, justResolved, track, resetWatch: resetWatchTracked, reload, rows };
}
